import React, { useEffect, useMemo, useRef, useState } from 'react';
import { formatDistanceToNow } from 'date-fns';
import { compareVersionsNewestFirst } from '../../utils/projectFileStreamVersions';
import { Send, X, Loader2, AlertCircle, History, Package } from 'lucide-react';
import {
  buildReleasedFilenameFromDeal,
  defaultDisplayLabelForKind,
  normalizeDisplayLabel,
  releaseProjectFileToPortal,
  setPortalReleaseLoomUrl,
  type ReleasableStreamKind,
} from '../../utils/portalFileReleaseService';
import { parseLoomVideoId } from '../../utils/loomEmbed';
import {
  PORTAL_DESIGN_PHASE_HINTS,
  PORTAL_DESIGN_PHASE_LABELS,
  PORTAL_DESIGN_PHASES,
  defaultDesignPhaseForTarget,
} from '../../utils/portalReleasePhase';
import { structureSelectionLabel, structureSelectionTitle } from '../../utils/structureSelectionLabel';
import type {
  EnhancedDeal,
  PortalDesignPhase,
  PortalFileRelease,
  PortalReleaseTarget,
  ProjectFile,
} from '../../types';
import './ReleaseToClientPortalModal.css';

const DISPLAY_LABEL_MAX = 60;

export interface ReleaseToClientPortalModalProps {
  portalId: string;
  dealId: string;
  streamKind: ReleasableStreamKind;
  streamTitle: string;
  versions: ProjectFile[];
  existingReleases: PortalFileRelease[];
  deal: Pick<EnhancedDeal, 'projectNumber' | 'clientLabel' | 'descriptor' | 'title'> | null | undefined;
  initialVersionId?: string | null;
  /** Structure names tagged on the source track (empty = whole project). Shown read-only. */
  structureNames?: string[];
  onClose: () => void;
  onReleased?: (result: {
    releaseId: string;
    releaseIds: string[];
    bundleId: string;
    releasedUrl: string;
    name: string;
    releaseVersionIndex: number;
  }) => void;
}

const ReleaseToClientPortalModal: React.FC<ReleaseToClientPortalModalProps> = ({
  portalId,
  dealId,
  streamKind,
  streamTitle,
  versions,
  existingReleases,
  deal,
  initialVersionId,
  structureNames,
  onClose,
  onReleased,
}) => {
  const sortedVersions = useMemo(
    () => [...versions].sort(compareVersionsNewestFirst),
    [versions],
  );
  const newestVersionId = sortedVersions[0]?.id || '';

  const defaultFileId = useMemo(() => {
    if (initialVersionId) return initialVersionId;
    return newestVersionId;
  }, [initialVersionId, newestVersionId]);

  const [releaseAsPackage, setReleaseAsPackage] = useState(false);
  const [selectedFileId, setSelectedFileId] = useState<string>(defaultFileId);
  const [selectedFileIds, setSelectedFileIds] = useState<Set<string>>(() => new Set(defaultFileId ? [defaultFileId] : []));
  const [primaryFileId, setPrimaryFileId] = useState<string>(defaultFileId);
  const [displayLabel, setDisplayLabel] = useState<string>(defaultDisplayLabelForKind(streamKind));
  const [bundleLabel, setBundleLabel] = useState<string>(defaultDisplayLabelForKind(streamKind));
  const [fileDisplayLabels, setFileDisplayLabels] = useState<Record<string, string>>({});
  const [portalTarget, setPortalTarget] = useState<PortalReleaseTarget>(
    streamKind === 'two_d_pdf' ? 'engineered_drawings' : 'model',
  );
  /** `null` = follow the default derived from destination / LOD; set when the PM overrides. */
  const [designPhaseOverride, setDesignPhaseOverride] = useState<PortalDesignPhase | null>(null);
  const [requestApproval, setRequestApproval] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loomUrl, setLoomUrl] = useState('');

  const dialogRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !submitting) {
        e.preventDefault();
        onClose();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose, submitting]);

  const selectedFile = useMemo(
    () => sortedVersions.find(v => v.id === selectedFileId) || null,
    [sortedVersions, selectedFileId],
  );

  const effectiveTarget: PortalReleaseTarget = streamKind === 'two_d_pdf' ? portalTarget : 'model';
  const phaseLodSource = useMemo(() => {
    if (!releaseAsPackage) return selectedFile;
    return sortedVersions.find(v => v.id === primaryFileId) || sortedVersions.find(v => selectedFileIds.has(v.id || '')) || null;
  }, [releaseAsPackage, selectedFile, sortedVersions, primaryFileId, selectedFileIds]);
  const defaultDesignPhase = defaultDesignPhaseForTarget(effectiveTarget, phaseLodSource?.detailStage ?? null);
  const designPhase: PortalDesignPhase = designPhaseOverride ?? defaultDesignPhase;
  const scopeNames = (structureNames ?? []).filter(Boolean);

  const nextReleaseVersionIndex = useMemo(() => {
    let max = 0;
    for (const r of existingReleases) {
      if (r.streamKind === streamKind && r.dealId === dealId) {
        if (r.releaseVersionIndex > max) max = r.releaseVersionIndex;
      }
    }
    return max + 1;
  }, [existingReleases, streamKind, dealId]);

  const filenamePreview = useMemo(
    () =>
      buildReleasedFilenameFromDeal({
        deal,
        sourceFileName: selectedFile?.name,
        displayLabel: releaseAsPackage ? bundleLabel : displayLabel,
        releaseVersionIndex: nextReleaseVersionIndex,
        kind: streamKind,
      }),
    [deal, selectedFile, displayLabel, bundleLabel, releaseAsPackage, nextReleaseVersionIndex, streamKind],
  );

  const packageCount = selectedFileIds.size;
  const canSubmit =
    !submitting &&
    !!portalId &&
    (releaseAsPackage ? packageCount > 0 : !!selectedFile);

  const togglePackageFile = (fileId: string) => {
    setSelectedFileIds(prev => {
      const next = new Set(prev);
      if (next.has(fileId)) {
        next.delete(fileId);
        if (primaryFileId === fileId) {
          const remaining = Array.from(next);
          setPrimaryFileId(remaining[0] || '');
        }
      } else {
        next.add(fileId);
        if (!primaryFileId) setPrimaryFileId(fileId);
      }
      return next;
    });
  };

  const getFileLabel = (fileId: string) =>
    fileDisplayLabels[fileId] ?? displayLabel;

  const handleSubmit = async () => {
    const trimmedLoom = loomUrl.trim();
    if (streamKind === 'three_d_html' && trimmedLoom && !parseLoomVideoId(trimmedLoom)) {
      setError('Paste a valid Loom link (https://www.loom.com/share/… or …/embed/…), or leave it blank.');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      if (releaseAsPackage) {
        const ids = Array.from(selectedFileIds);
        if (ids.length === 0) {
          setError('Select at least one file for the package.');
          return;
        }
        const labels: Record<string, string> = {};
        for (const id of ids) {
          labels[id] = normalizeDisplayLabel(getFileLabel(id), streamKind);
        }
        const result = await releaseProjectFileToPortal({
          portalId,
          dealId,
          projectFileIds: ids,
          bundleLabel,
          displayLabel: bundleLabel,
          primaryProjectFileId: primaryFileId || ids[0],
          fileDisplayLabels: labels,
          portalTarget: effectiveTarget,
          designPhase,
          requestApproval,
        });
        if (streamKind === 'three_d_html' && trimmedLoom && result.releaseId) {
          await setPortalReleaseLoomUrl(portalId, result.releaseId, trimmedLoom);
        }
        onReleased?.(result);
        onClose();
      } else {
        if (!selectedFile?.id) {
          setError('Pick a version to release.');
          return;
        }
        const result = await releaseProjectFileToPortal({
          portalId,
          dealId,
          projectFileId: selectedFile.id,
          displayLabel,
          portalTarget: effectiveTarget,
          designPhase,
          requestApproval,
        });
        if (streamKind === 'three_d_html' && trimmedLoom && result.releaseId) {
          await setPortalReleaseLoomUrl(portalId, result.releaseId, trimmedLoom);
        }
        onReleased?.(result);
        onClose();
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Could not release this file. Please try again.';
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="pm-release-modal-overlay"
      role="presentation"
      onClick={() => {
        if (!submitting) onClose();
      }}
    >
      <div
        className="pm-release-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="pm-release-modal-title"
        ref={dialogRef}
        onClick={e => e.stopPropagation()}
      >
        <header className="pm-release-modal-header">
          <span className="pm-release-modal-icon" aria-hidden>
            <Send size={18} />
          </span>
          <div>
            <h3 id="pm-release-modal-title">Release to client portal</h3>
            <p className="pm-release-modal-subtitle">
              {streamTitle || (streamKind === 'two_d_pdf' ? '2D PDF' : '3D HTML')}
            </p>
          </div>
          <button
            type="button"
            className="pm-release-modal-close"
            onClick={() => {
              if (!submitting) onClose();
            }}
            aria-label="Close"
            disabled={submitting}
          >
            <X size={16} aria-hidden />
          </button>
        </header>

        <div className="pm-release-modal-body">
          <p className="pm-release-modal-hint">
            Choose which version to push to the client portal. The client will see this file in their
            portal version history. Releases stay in history forever.
          </p>

          <label className="pm-release-package-toggle">
            <input
              type="checkbox"
              checked={releaseAsPackage}
              onChange={e => setReleaseAsPackage(e.target.checked)}
              disabled={submitting}
            />
            <Package size={14} aria-hidden />
            Release as package (multiple files, one version)
          </label>

          <div
            className="pm-release-version-list"
            role="group"
            aria-labelledby="pm-release-version-heading"
          >
            <div id="pm-release-version-heading" className="pm-release-modal-section-label">
              {releaseAsPackage ? 'Files in package' : 'Version'}
            </div>
            {sortedVersions.length === 0 ? (
              <div className="pm-release-empty">No versions available in this history.</div>
            ) : (
              sortedVersions.map(v => {
                const fid = v.id || '';
                const isSel = releaseAsPackage ? selectedFileIds.has(fid) : v.id === selectedFileId;
                return (
                  <label
                    key={v.id}
                    className={`pm-release-version-row ${isSel ? 'pm-release-version-row--active' : ''}`}
                  >
                    <input
                      type={releaseAsPackage ? 'checkbox' : 'radio'}
                      name="pm-release-version"
                      value={fid}
                      checked={isSel}
                      onChange={() => {
                        if (releaseAsPackage) {
                          togglePackageFile(fid);
                        } else if (v.id) {
                          setSelectedFileId(v.id);
                        }
                      }}
                      disabled={submitting || !v.id}
                    />
                    <span className="pm-release-version-main">
                      <span className="pm-release-version-title">
                        {v.versionLabel || `Version ${v.versionIndex ?? ''}`.trim()}
                        {v.versionIndex != null ? (
                          <span className="pm-release-version-index">{`#${v.versionIndex}`}</span>
                        ) : null}
                        {v.id && v.id === newestVersionId ? <span className="pm-release-version-head">Latest</span> : null}
                      </span>
                      <span className="pm-release-version-meta">
                        {v.uploadedByName || 'Unknown'}
                        <span aria-hidden> · </span>
                        {formatDistanceToNow(v.uploadedAt, { addSuffix: true })}
                      </span>
                      {v.versionNote?.trim() ? (
                        <span className="pm-release-version-note">{v.versionNote.trim()}</span>
                      ) : null}
                      {releaseAsPackage && isSel && (
                        <span className="pm-release-file-label-row">
                          <input
                            type="text"
                            className="pm-release-modal-input pm-release-file-label-input"
                            value={getFileLabel(fid)}
                            maxLength={DISPLAY_LABEL_MAX}
                            placeholder="Client label for this file"
                            onChange={e =>
                              setFileDisplayLabels(prev => ({ ...prev, [fid]: e.target.value }))
                            }
                            onClick={e => e.stopPropagation()}
                            disabled={submitting}
                          />
                          {/* Span (not nested <label>) — outer row is already a label. */}
                          <span className="pm-release-primary-pick">
                            <input
                              type="radio"
                              name="pm-release-primary"
                              checked={primaryFileId === fid}
                              onChange={() => setPrimaryFileId(fid)}
                              disabled={submitting}
                            />
                            Primary link
                          </span>
                        </span>
                      )}
                    </span>
                  </label>
                );
              })
            )}
          </div>

          <div className="pm-release-modal-field">
            <label htmlFor="pm-release-display-label" className="pm-release-modal-section-label">
              {releaseAsPackage ? 'Package label' : 'Display label'}
            </label>
            <input
              id="pm-release-display-label"
              type="text"
              className="pm-release-modal-input"
              value={releaseAsPackage ? bundleLabel : displayLabel}
              maxLength={DISPLAY_LABEL_MAX}
              onChange={e =>
                releaseAsPackage ? setBundleLabel(e.target.value) : setDisplayLabel(e.target.value)
              }
              placeholder={defaultDisplayLabelForKind(streamKind)}
              disabled={submitting}
            />
            <p className="pm-release-modal-hint-row">What the client will see in their portal.</p>
          </div>

          {streamKind === 'three_d_html' && (
            <div className="pm-release-modal-field">
              <label htmlFor="pm-release-loom-url" className="pm-release-modal-section-label">
                Loom walkthrough for this version
              </label>
              <input
                id="pm-release-loom-url"
                type="url"
                className="pm-release-modal-input"
                value={loomUrl}
                onChange={e => setLoomUrl(e.target.value)}
                placeholder="https://www.loom.com/share/… (optional)"
                disabled={submitting}
              />
              <p className="pm-release-modal-hint-row">
                Leave blank if this version has no video. Clients will not see a previous Loom.
              </p>
            </div>
          )}

          {streamKind === 'two_d_pdf' && (
            <div className="pm-release-modal-field">
              <span className="pm-release-modal-label">Portal destination</span>
              <div className="pm-release-modal-radio-group">
                <label className="pm-release-modal-radio">
                  <input
                    type="radio"
                    name="portal-target"
                    checked={portalTarget === 'engineered_drawings'}
                    onChange={() => setPortalTarget('engineered_drawings')}
                    disabled={submitting}
                  />
                  Engineered Drawings (post-contract)
                </label>
                <label className="pm-release-modal-radio">
                  <input
                    type="radio"
                    name="portal-target"
                    checked={portalTarget === 'concept_elevations'}
                    onChange={() => setPortalTarget('concept_elevations')}
                    disabled={submitting}
                  />
                  Concept — 3D Model phase
                </label>
              </div>
            </div>
          )}

          <div className="pm-release-modal-field" data-tour="pf-release-phase">
            <span className="pm-release-modal-label">
              Design phase
              <span className="pm-release-scope-chip" title={structureSelectionTitle(scopeNames)}>
                {structureSelectionLabel(scopeNames)}
              </span>
            </span>
            <div className="pm-release-modal-radio-group pm-release-modal-radio-group--inline">
              {PORTAL_DESIGN_PHASES.map(phase => (
                <label key={phase} className="pm-release-modal-radio" title={PORTAL_DESIGN_PHASE_HINTS[phase]}>
                  <input
                    type="radio"
                    name="design-phase"
                    checked={designPhase === phase}
                    onChange={() => setDesignPhaseOverride(phase === defaultDesignPhase ? null : phase)}
                    disabled={submitting}
                  />
                  {PORTAL_DESIGN_PHASE_LABELS[phase]}
                  {phase === defaultDesignPhase ? (
                    <span className="pm-release-phase-default">suggested</span>
                  ) : null}
                </label>
              ))}
            </div>
            <p className="pm-release-modal-hint-row">
              Where this release sits on the client&apos;s approval trail. Suggested from the destination
              {effectiveTarget === 'model' ? ' and the version\u2019s LOD' : ''}.
            </p>
          </div>

          <label className="pm-release-package-toggle pm-release-approval-toggle">
            <input
              type="checkbox"
              checked={requestApproval}
              onChange={e => setRequestApproval(e.target.checked)}
              disabled={submitting}
            />
            Request client approval
            <span className="pm-release-modal-hint-row pm-release-approval-hint">
              Shows as &ldquo;awaiting your approval&rdquo; on the portal. Untick for informational releases.
            </span>
          </label>

          <div className="pm-release-modal-preview">
            <span className="pm-release-modal-preview-label">
              {releaseAsPackage && packageCount > 1
                ? `Release v${nextReleaseVersionIndex} · ${packageCount} files`
                : 'Filename the client sees'}
            </span>
            <code className="pm-release-modal-preview-name" title={filenamePreview}>
              {releaseAsPackage && packageCount > 1
                ? `${bundleLabel} — v${nextReleaseVersionIndex} (${packageCount} files)`
                : filenamePreview}
            </code>
            {existingReleases.some(r => r.streamKind === streamKind && r.dealId === dealId) && (
              <span className="pm-release-modal-preview-history">
                <History size={11} aria-hidden /> Release v{nextReleaseVersionIndex} · prior releases stay in history
              </span>
            )}
          </div>

          {error && (
            <div className="pm-release-modal-error" role="alert">
              <AlertCircle size={14} aria-hidden /> {error}
            </div>
          )}
        </div>

        <footer className="pm-release-modal-actions">
          <button
            type="button"
            className="pm-release-modal-btn pm-release-modal-btn--ghost"
            onClick={onClose}
            disabled={submitting}
          >
            Cancel
          </button>
          <button
            type="button"
            className="pm-release-modal-btn pm-release-modal-btn--primary"
            onClick={handleSubmit}
            disabled={!canSubmit}
            aria-label="Release this version to the client portal"
          >
            {submitting ? (
              <>
                <Loader2 size={14} className="pm-release-modal-spin" aria-hidden /> Releasing…
              </>
            ) : (
              <>
                <Send size={14} aria-hidden /> {releaseAsPackage ? 'Release package' : 'Release to portal'}
              </>
            )}
          </button>
        </footer>
      </div>
    </div>
  );
};

export default ReleaseToClientPortalModal;
